/*
 * A pet's profile photo.
 *
 * Photos live in the backend's private `pet-images` storage, one folder per
 * pet — the same place the groomer's admin uploads pet photos to. Every
 * surface treats the FIRST photo as the pet's avatar, and the backend lists
 * a client's own photo (`profile-*`) first, newest first. So:
 *   - a photo the client picks here is what the groomer sees too;
 *   - a pet with no client photo shows the groomer's first photo, here and
 *     in the admin alike;
 *   - a pet with neither shows the dog / cat icon.
 *
 * Read links expire after 30 minutes, so they are held in memory only
 * (never localStorage) and fetched again when stale or when an <img>
 * reports the link dead. Uploading goes straight from the browser to
 * storage with a short-lived write link from the CMS.
 *
 * Avatars (24–64 px) show the photo's small copy (`smallUrl`, 128 px, a few
 * KB; mpgadmin D80), which the API makes the first time the photo is listed
 * and lists beside it; the pet form's photo slot shows the photo itself
 * (`url`). An API that doesn't send the small copy yet, or a photo it couldn't
 * make one of, gives `smallUrl` null, and the avatar shows `url` as before.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { CMS_BASE_URL } from '../config/api.config';
import { getCMSJWT } from './auth.service';

export interface PetPhoto {
  /** The read link, or null when the pet has no photo at all. */
  url: string | null;
  /** The same photo's small copy for avatars (128 px), or null when there is none: show `url`. */
  smallUrl: string | null;
  /** True when the photo is one the client chose here (so it can be removed). */
  isProfile: boolean;
}

/** One photo as the CMS lists it: its link and, on the avatar, its small copy's. */
export interface ListedPhoto {
  url: string;
  smallUrl: string | null;
}

/** Read links last 30 minutes; refresh well before. */
const STALE_MS = 20 * 60 * 1000;
const MAX_SIDE = 800;
const JPEG_QUALITY = 0.85;
const MAX_INPUT_BYTES = 25 * 1024 * 1024;

interface Entry extends PetPhoto {
  fetchedAt: number;
}

const cache = new Map<number, Entry>();
const inflight = new Map<number, Promise<PetPhoto>>();
const listeners = new Set<() => void>();
let version = 0;
const emit = () => {
  version += 1;
  listeners.forEach(l => l());
};

const authHeaders = (): Record<string, string> => {
  const jwt = getCMSJWT();
  if (!jwt) throw new Error('Please sign in to add a photo.');
  return { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' };
};

/** The blob's file name inside the pet's folder, from a storage link. */
export const fileNameOf = (url: string): string => {
  const path = url.split('?')[0];
  return decodeURIComponent(path.slice(path.lastIndexOf('/') + 1));
};

const isProfileName = (name: string) => name.toLowerCase().startsWith('profile-');

/**
 * The pet's photo from its list: the newest `profile-*`, else the first.
 * The backend already orders them this way; doing it here as well keeps
 * the storefront right against a backend that has not been redeployed.
 */
export const pickPhoto = (urls: string[]): PetPhoto => pickListedPhoto(urls.map(url => ({ url, smallUrl: null })));

/**
 * As `pickPhoto`, keeping the chosen photo's own small copy: the API lists
 * one beside the avatar only, so a photo chosen here that isn't the API's
 * first has none, and its avatar shows the photo itself.
 */
export const pickListedPhoto = (photos: ListedPhoto[]): PetPhoto => {
  const profile = photos
    .filter(p => isProfileName(fileNameOf(p.url)))
    .sort((a, b) => (fileNameOf(a.url) < fileNameOf(b.url) ? 1 : -1));
  const chosen = profile[0] ?? photos[0];
  return { url: chosen?.url ?? null, smallUrl: chosen?.smallUrl ?? null, isProfile: profile.length > 0 };
};

/** A small copy's link, only when it is a link (anything else: none, and the avatar shows the photo). */
const linkOrNull = (v: unknown): string | null => (typeof v === 'string' && /^https:\/\//.test(v) ? v : null);

const listPhotos = async (petId: number): Promise<ListedPhoto[]> => {
  const res = await fetch(`${CMS_BASE_URL}/api/pet/image?id=${petId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Could not load the pet's photos (${res.status}).`);
  const data = await res.json();
  return Array.isArray(data)
    ? data
        .filter((d: any) => typeof d?.petImageSASUrl === 'string' && d.petImageSASUrl)
        .map((d: any) => ({ url: d.petImageSASUrl as string, smallUrl: linkOrNull(d?.smallSasUrl) }))
    : [];
};

const listPhotoUrls = async (petId: number): Promise<string[]> => (await listPhotos(petId)).map(p => p.url);

/** The pet's photo, from memory when fresh, else from the server (one request per pet at a time). */
export const loadPetPhoto = (petId: number, force = false): Promise<PetPhoto> => {
  const hit = cache.get(petId);
  if (!force && hit && Date.now() - hit.fetchedAt < STALE_MS) return Promise.resolve(hit);
  const running = inflight.get(petId);
  if (running) return running;
  const p = (async () => {
    try {
      const photo = pickListedPhoto(await listPhotos(petId));
      cache.set(petId, { ...photo, fetchedAt: Date.now() });
      emit();
      return photo;
    } catch (err) {
      console.error('Loading pet photo failed:', err);
      // Remember "none" briefly so a failing call is not retried on every render.
      const none: PetPhoto = { url: null, smallUrl: null, isProfile: false };
      cache.set(petId, { ...none, fetchedAt: Date.now() - STALE_MS + 60_000 });
      emit();
      return none;
    } finally {
      inflight.delete(petId);
    }
  })();
  inflight.set(petId, p);
  return p;
};

/**
 * Start the photos of pets about to be shown: the services step does it
 * for the pets this phone already knows while the account's list is still
 * on its way, so a photo no longer waits for that list before it even
 * asks for its link, then for the link before it downloads. The picture
 * started is the one the avatars show: the small copy when there is one.
 * The link and the downloaded picture stay in memory (G2); nothing is stored.
 */
export const prefetchPetPhotos = (petIds: number[]): void => {
  if (!getCMSJWT()) return;
  petIds
    .filter(id => Number.isInteger(id) && id > 0)
    .forEach(id => {
      void loadPetPhoto(id).then(photo => {
        const shown = photo.smallUrl ?? photo.url;
        if (!shown || typeof Image === 'undefined') return;
        const img = new Image();
        img.decoding = 'async';
        img.src = shown;
      });
    });
};

const sasUrl = async (petId: number, fileName: string, operation: 'upload' | 'delete'): Promise<string> => {
  // `operation` must be exactly "upload": the backend treats anything else
  // (even "Upload") as a delete.
  const res = await fetch(
    `${CMS_BASE_URL}/api/pet/image?id=${petId}&fileName=${encodeURIComponent(fileName)}&operation=${operation}`,
    { method: 'POST', headers: authHeaders() }
  );
  if (!res.ok) throw new Error(`Could not prepare the photo (${res.status}).`);
  const data = await res.json();
  if (!data?.sasUrl) throw new Error('No upload link came back. Please try again.');
  return data.sasUrl as string;
};

const deleteBlob = async (petId: number, fileName: string) => {
  const url = await sasUrl(petId, fileName, 'delete');
  const res = await fetch(url, { method: 'DELETE' });
  if (!res.ok && res.status !== 404) throw new Error(`Could not remove the old photo (${res.status}).`);
};

/**
 * A phone photo made small enough to upload quickly: JPEG, longest side
 * 800px. Re-encoding also drops location data, and turns an iPhone HEIC
 * photo into a JPEG every browser can show (iOS decodes HEIC for us).
 */
export const shrinkPhoto = async (file: File): Promise<Blob> => {
  if (file.size > MAX_INPUT_BYTES) throw new Error('That photo is too large. Please pick one under 25MB.');
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("That photo couldn't be opened. Please try a JPEG or PNG."));
      i.src = objectUrl;
    });
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('This browser cannot prepare photos.');
    ctx.fillStyle = '#fff'; // transparent PNGs get a white background, not black
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error("That photo couldn't be prepared. Please try another.");
    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Make `file` the pet's profile photo: shrink it, upload it, then remove the
 * client's earlier profile photos (never the groomer's photos).
 */
export const setPetPhoto = async (petId: number, file: File): Promise<PetPhoto> => {
  const jpeg = await shrinkPhoto(file);
  const url = await sasUrl(petId, `profile-${Date.now()}.jpg`, 'upload');
  const put = await fetch(url, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'image/jpeg' },
    body: jpeg
  });
  if (!put.ok) throw new Error(put.status === 403 ? 'The upload link expired. Please try again.' : `Upload failed (${put.status}).`);
  const newName = fileNameOf(url);
  try {
    const older = (await listPhotoUrls(petId)).map(fileNameOf).filter(n => isProfileName(n) && n !== newName);
    await Promise.all(older.map(n => deleteBlob(petId, n)));
  } catch (err) {
    // The new photo is up and sorts first; a leftover old one is harmless.
    console.error('Removing the previous profile photo failed:', err);
  }
  return loadPetPhoto(petId, true);
};

/** Remove the client's profile photo(s). A groomer's photo, if any, becomes the avatar again. */
export const removePetPhoto = async (petId: number): Promise<PetPhoto> => {
  const names = (await listPhotoUrls(petId)).map(fileNameOf).filter(isProfileName);
  await Promise.all(names.map(n => deleteBlob(petId, n)));
  return loadPetPhoto(petId, true);
};

/** A sign-out: nothing about the last account's pets stays in memory. */
export const clearPetPhotos = () => {
  cache.clear();
  inflight.clear();
  emit();
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getVersion = () => version;

/**
 * The pet's photo for a component. Only account pets (positive ids) of a
 * signed-in client have photos; anything else gets `{ url: null }`.
 */
export const usePetPhoto = (petId: number | undefined): PetPhoto & { refresh: () => void } => {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  const eligible = !!petId && petId > 0 && !!getCMSJWT();
  const entry = eligible ? cache.get(petId as number) : undefined;
  const stale = !entry || Date.now() - entry.fetchedAt >= STALE_MS;
  useEffect(() => {
    if (eligible && stale) loadPetPhoto(petId as number);
  }, [eligible, stale, petId]);
  return {
    url: entry?.url ?? null,
    smallUrl: entry?.smallUrl ?? null,
    isProfile: entry?.isProfile ?? false,
    refresh: () => {
      if (eligible) loadPetPhoto(petId as number, true);
    }
  };
};
